/**
 * scheduleLayout.js — the per-type schedule column/group layout, shared by the SpecEditor (reference
 * only now), the Schedules branch/editor, and (mirrored server-side) the schedule + spec PDFs.
 *
 * Each column is [label, [attrKeys…]]; keys are tried in order (first non-empty wins) so override
 * fields take precedence and '_label'/'_room'/'_section' map to the structural columns.
 * groupBy: 'spec_section' | 'room_string' | 'none'.
 *
 * KEEP IN SYNC with the SCHEDULE_SPEC copy in api/schedule-pdf.js + api/spec-pdf.js (server can't import).
 */

// The eight schedule types, in display order.
export const SCHEDULE_TYPES = [
  'Colors', 'Appliances', 'Plumbing Fixtures', 'Bath Accessories',
  'Electrical Fixtures', 'Casework', 'Hardware', 'Mechanical Units',
]

export const SCHEDULE_TYPE_BY_BASE = {
  colors: 'Colors', casework: 'Casework', plumbing_fixtures: 'Plumbing Fixtures',
  bath_accessories: 'Bath Accessories', electrical_fixtures: 'Electrical Fixtures',
  appliances: 'Appliances', hardware: 'Hardware', mechanical_units: 'Mechanical Units',
}
export function scheduleTypeForSource(sv) {
  if (!sv) return null
  const base = String(sv).replace(/_schedule_items?$/, '')
  return SCHEDULE_TYPE_BY_BASE[base] || null
}

export const SCHEDULE_SPEC = {
  Colors: { groupBy: 'spec_section', columns: [
    ['Material', ['Color', '_label']], ['Primary Material', ['Primary Material', 'Material Type']],
    ['Manufacturer / Series', ['Manufacturer Series']], ['Finish', ['Finish']], ['Location', ['Location', 'Locations']],
  ] },
  Appliances: { groupBy: 'room_string', columns: [
    ['Appliance', ['Appliance Type', '_label']], ['Manufacturer / Series', ['Manufacturer Series']],
    ['Model', ['Model Number']], ['Finish', ['Finish']],
  ] },
  'Bath Accessories': { groupBy: 'room_string', columns: [
    ['Accessory', ['Bath Accessory', '_label']], ['Manufacturer / Series', ['Manufacturer Series']],
    ['Model', ['Model Number']], ['Finish', ['Finish']],
  ] },
  'Plumbing Fixtures': { groupBy: 'room_string', columns: [
    ['Fixture', ['Plumbing Fixture', '_label']], ['Manufacturer / Series', ['Manufacturer Series']],
    ['Model', ['Model Number']], ['Finish', ['Finish']],
  ] },
  Casework: { groupBy: 'room_string', columns: [
    ['Casework', ['Name', '_label']], ['Construction', ['Cabinet Construction']],
    ['Material / Species', ['Primary Material Species']], ['Finish System', ['Finish System']],
    ['Hardware Finish', ['Casework Hardware Finish']],
  ] },
  Hardware: { groupBy: 'none', columns: [
    ['Set', ['Name', '_label']], ['Handle', ['Handle Override', 'Handle']],
    ['Lockset', ['Lockset Override', 'Lockset']], ['Hinges', ['Hinges']], ['Finish', ['Hardware Finish']],
  ] },
  'Electrical Fixtures': { groupBy: 'room_string', columns: [
    ['Fixture', ['Electrical Fixture Name', '_label']], ['Use', ['Electrical Fixture Use']],
    ['Manufacturer / Series', ['Electrical Manufacture Series Override', 'Electrical Manufacturer Series']],
    ['Model', ['Electrical Model Number Override', 'Electrical Model Number']],
    ['Location', ['Location', 'Location from Electrical Fixture']],
  ] },
  default: { groupBy: 'room_string', columns: [
    ['Item', ['_label']], ['Manufacturer / Series', ['Manufacturer Series']], ['Model', ['Model Number']], ['Finish', ['Finish']],
  ] },
}

export function scheduleSpecFor(type) { return SCHEDULE_SPEC[type] || SCHEDULE_SPEC.default }

/** Resolve a cell value by trying each candidate key in order (first non-empty wins). */
export function scheduleCell(it, keys) {
  for (const key of keys) {
    let v
    if (key === '_label') v = it.item_label
    else if (key === '_room') v = it.room_string
    else if (key === '_section') v = it.spec_section
    else v = it.attrs ? it.attrs[key] : null
    if (v == null || v === '') continue
    return Array.isArray(v) ? v.join(', ') : (typeof v === 'object' ? '' : String(v))
  }
  return ''
}

/**
 * Editor field list for a type: the first column is the item's primary label (→ item_label); the rest
 * become editable attrs keyed by their first (canonical) attr key. Structural-only columns are skipped.
 * Returns [{ label, target }] where target = { kind:'item_label' } | { kind:'attr', key }.
 */
export function editorFieldsForType(type) {
  const spec = scheduleSpecFor(type)
  const out = []
  spec.columns.forEach((col, i) => {
    const [label, keys] = col
    if (i === 0) { out.push({ label, target: { kind: 'item_label' } }); return }
    const key = (keys || []).find(k => k && !k.startsWith('_'))
    if (key) out.push({ label, target: { kind: 'attr', key } })
  })
  return out
}

/**
 * FULL editable property schema per schedule type — the curated set of product fields a user
 * authors in the Schedule Builder (richer than the display columns in SCHEDULE_SPEC). Each entry is
 * [label, key, input?] where key '_label' maps to item_label and input ∈ 'text'|'textarea'|'checkbox'
 * (default 'text'). Keys map straight onto attrs. Derived/AI/system keys are intentionally excluded.
 */
export const SCHEDULE_EDIT_FIELDS = {
  Colors: [
    ['Material', '_label'], ['Color', 'Color'], ['Primary Material', 'Primary Material'],
    ['Manufacturer / Series', 'Manufacturer Series'], ['Finish', 'Finish'], ['Location', 'Location'],
    ['Remarks', 'Remarks', 'textarea'], ['Show in PDF schedule', 'Show in PDF Schedule', 'checkbox'],
  ],
  Appliances: [
    ['Appliance', '_label'], ['Manufacturer / Series', 'Manufacturer Series'], ['Model', 'Model Number'],
    ['Finish', 'Finish'], ['Remarks', 'Remarks', 'textarea'], ['Show in PDF schedule', 'Show in PDF Schedule', 'checkbox'],
  ],
  'Bath Accessories': [
    ['Accessory', '_label'], ['Manufacturer / Series', 'Manufacturer Series'], ['Model', 'Model Number'],
    ['Finish', 'Finish'], ['Remarks', 'Remarks', 'textarea'], ['Show in PDF schedule', 'Show in PDF Schedule', 'checkbox'],
  ],
  'Plumbing Fixtures': [
    ['Fixture', '_label'], ['Manufacturer / Series', 'Manufacturer Series'], ['Model', 'Model Number'],
    ['Finish', 'Finish'], ['Remarks', 'Remarks', 'textarea'], ['Show in PDF schedule', 'Show in PDF Schedule', 'checkbox'],
  ],
  Casework: [
    ['Casework', '_label'], ['Construction', 'Cabinet Construction'], ['Material / Species', 'Primary Material Species'],
    ['Finish System', 'Finish System'], ['Hardware Finish', 'Casework Hardware Finish'],
    ['Door Mount Style', 'Door Mount Style'], ['Panel Style', 'Panel Style'],
    ['Cabinet Pull', 'Cabinet Pull'], ['Drawer Pull', 'Drawer Pull'],
    ['Description', 'Casework Description', 'textarea'], ['Show in PDF schedule', 'Show in PDF Schedule', 'checkbox'],
  ],
  Hardware: [
    ['Set', '_label'], ['Handle', 'Handle'], ['Lockset', 'Lockset'], ['Hinges', 'Hinges'], ['Closer', 'Closer'],
    ['Stop', 'Stop'], ['Kickplate', 'Kickplate'], ['Weatherstripping', 'Weatherstripping'],
    ['Electric Strike', 'Electric Strike'], ['Keypad', 'Keypad'], ['Finish', 'Hardware Finish'],
    ['Show in PDF schedule', 'Show in PDF Schedule', 'checkbox'],
  ],
  'Electrical Fixtures': [
    ['Fixture', '_label'], ['Use', 'Electrical Fixture Use'], ['Manufacturer / Series', 'Electrical Manufacturer Series'],
    ['Model', 'Electrical Model Number'], ['Location', 'Location'], ['Wattage', 'Wattage'], ['Lumens', 'Lumens'],
    ['Quantity', 'Quantity'], ['Options', 'Electrical Options'], ['Remarks', 'Electrical Remarks', 'textarea'],
    ['Show in PDF schedule', 'Show in PDF Schedule', 'checkbox'],
  ],
  default: [
    ['Item', '_label'], ['Manufacturer / Series', 'Manufacturer Series'], ['Model', 'Model Number'],
    ['Finish', 'Finish'], ['Remarks', 'Remarks', 'textarea'], ['Show in PDF schedule', 'Show in PDF Schedule', 'checkbox'],
  ],
}

/** Resolve the full editable field schema for a type → [{ label, target, input }]. */
export function editFieldsForType(type) {
  const cols = SCHEDULE_EDIT_FIELDS[type] || SCHEDULE_EDIT_FIELDS.default
  return cols.map(([label, key, input]) => ({
    label,
    input: input || 'text',
    target: key === '_label' ? { kind: 'item_label' } : { kind: 'attr', key },
  }))
}

/**
 * Per-type TABLE column layout for the Schedule Builder grid. Each column is [label, key, kind]:
 *   kind 'image'   — thumbnail (key '_image')
 *        'label'   — the item's primary name (key '_label' → item_label)
 *        'section' — spec section (key '_section' → spec_section)
 *        'room'    — room(s) (key '_room' → room_string)
 *        'text'    — single-line attrs value (key = attrs key)
 *        'textarea'— multi-line attrs value
 *        'url'     — link attrs value (rendered clickable when not editing)
 *        'date'    — date attrs value (read-only display; set elsewhere)
 *        'linkedItems' — chips referencing OTHER schedule items (key = attrs array of {id,label});
 *                  `opt` on the column names the target schedule_type to pick from (e.g. 'Hardware').
 *                  Candidates are read from whatever's already loaded (no extra fetch) and the value
 *                  is a live reference — editing the linked item's own fields is reflected wherever
 *                  it's referenced, it is never copied/duplicated onto the referencing item.
 * Editable inline on unposted (open) milestone members; read-only once issued.
 * Colors mirrors the firm's existing schedule layout; refine the others to match per type.
 */
export const SCHEDULE_TABLE_COLUMNS = {
  Colors: [
    ['Image', '_image', 'image'], ['Spec Section', '_section', 'section'], ['Material', '_label', 'label'],
    ['Manufacturer / Series', 'Manufacturer Series', 'text'], ['Color / Pattern', 'Color', 'text'],
    ['Finish', 'Finish', 'text'], ['Room(s)', '_room', 'room'], ['Location', 'Location', 'text'],
    ['Vendor', 'Vendor', 'text'], ['Remarks', 'Remarks', 'textarea'],
  ],
  Appliances: [
    ['Image', '_image', 'image'], ['Appliance Type', '_label', 'label', 'Appliance Type'], ['Room(s)', '_room', 'room'],
    ['Manufacturer / Series', 'Manufacturer Series', 'text'], ['Model Number', 'Model Number', 'text'],
    ['Finish', 'Finish', 'text'], ['Remarks', 'Remarks', 'textarea'], ['Link', 'Link', 'url'],
  ],
  'Bath Accessories': [
    ['Image', '_image', 'image'], ['Accessory', '_label', 'label', 'Bath Accessory'], ['Room(s)', '_room', 'room'],
    ['Manufacturer / Series', 'Manufacturer Series', 'text'], ['Model Number', 'Model Number', 'text'],
    ['Finish', 'Finish', 'text'], ['Remarks', 'Remarks', 'textarea'], ['Link', 'Link', 'url'],
  ],
  'Plumbing Fixtures': [
    ['Image', '_image', 'image'], ['Plumbing Fixture', '_label', 'label', 'Plumbing Fixture'], ['Room(s)', '_room', 'room'],
    ['Manufacturer / Series', 'Manufacturer Series', 'text'], ['Model Number', 'Model Number', 'text'],
    ['Finish', 'Finish', 'text'], ['Remarks', 'Remarks', 'textarea'], ['Link', 'Link', 'url'],
  ],
  Casework: [
    ['Image', '_image', 'image'], ['Casework', '_label', 'label'], ['Room(s)', '_room', 'room'],
    ['Casework Types', 'Casework Types', 'text', 'Casework Types'], ['Cabinet Construction', 'Cabinet Construction', 'text', 'Cabinet Construction'],
    ['Door Mount Style', 'Door Mount Style', 'text', 'Door Mount Style'], ['Panel Style', 'Panel Style', 'text', 'Panel Style'],
    ['Panel Insert Type', 'Panel Insert Types', 'text', 'Panel Insert Types'], ['Primary Material / Species', 'Primary Material Species', 'text', 'Primary Material Species'],
    ['Accent Material', 'Accent Material', 'text', 'Accent Material'], ['Finish System', 'Finish System', 'text', 'Finish System'],
    ['Hardware Finish', 'Casework Hardware Finish', 'text', 'Casework Hardware Finish'],
    ['Hardware', 'Hardware Items', 'linkedItems', 'Hardware'],
    ['Drawer Pull', 'Drawer Pull', 'text'],
    ['Cabinet Pull', 'Cabinet Pull', 'text'], ['Manufacturer / Series', 'Manufacturer Series', 'text'],
    ['Model Number', 'Model Number', 'text'], ['Link', 'Link', 'url'],
  ],
  Hardware: [
    ['Image', '_image', 'image'], ['Hardware Set', '_label', 'label'], ['Finish', 'Hardware Finish', 'text', 'Hardware Finish'],
    ['Lockset', 'Lockset', 'text'], ['Handle', 'Handle', 'text'], ['Keypad', 'Keypad', 'text'],
    ['Electric Strike', 'Electric Strike', 'text'], ['Hinges', 'Hinges', 'text'], ['Stop', 'Stop', 'text'],
    ['Closer', 'Closer', 'text'],
  ],
  'Electrical Fixtures': [
    ['Image', '_image', 'image'], ['Name', '_label', 'label'], ['Room(s)', '_room', 'room'], ['Location', 'Location', 'text'],
    ['Manuf / Series', 'Electrical Manufacturer Series', 'text'], ['Model No', 'Electrical Model Number', 'text'],
    ['Options', 'Electrical Options', 'text'], ['Wattage', 'Wattage', 'text'], ['Lumens', 'Lumens', 'text'],
    ['Remarks', 'Electrical Remarks', 'textarea'], ['Link', 'Electrical Link Override', 'url'],
  ],
  default: [
    ['Image', '_image', 'image'], ['Item', '_label', 'label'], ['Manufacturer / Series', 'Manufacturer Series', 'text'],
    ['Model', 'Model Number', 'text'], ['Finish', 'Finish', 'text'], ['Room(s)', '_room', 'room'],
    ['Remarks', 'Remarks', 'textarea'],
  ],
}

/** Table column layout for a type → [{ label, key, kind, opt }]. `opt` (when set) names a fixed
 *  option list in SCHEDULE_FIELD_OPTIONS, so the cell renders as a constrained dropdown. */
export function tableColumnsForType(type) {
  const cols = SCHEDULE_TABLE_COLUMNS[type] || SCHEDULE_TABLE_COLUMNS.default
  return cols.map(([label, key, kind, opt]) => ({ label, key, kind, opt: opt || null }))
}

/**
 * Constrained option lists for schedule fields — the firm's vocabulary, lifted verbatim from the
 * Airtable Schedule Items single-select fields. A column whose 4th entry names one of these renders
 * as a dropdown (no free text), so designers pick from the approved set instead of typing.
 * (Source of truth today; a future enhancement is to make these editable in Supabase.)
 */
export const SCHEDULE_FIELD_OPTIONS = {
  'Appliance Type': ['Beverage Refrigerator', 'Clothes Dryer', 'Clothes Washing Machine', 'Dishwasher', 'Freezer', 'Hood Insert', 'Ice Maker', 'Microwave', 'Microwave Drawer', 'Oven Range', 'Range Top', 'Refrigerator', 'Trash Compactor', 'Wall Oven', 'Warming Drawer', 'Wine Refrigerator'],
  'Plumbing Fixture': ['Air Gap', 'Bath Tub', 'Bath Tub and Shower Faucet Set', 'Bath Tub Drain', 'Disposal ', 'Disposal Air Switch', 'Disposal Flange and Stopper', 'Hand Shower', 'Pot Filler', 'Shower Controls', 'Shower Drain', 'Shower Head', 'Shower Head Arm and Flange', 'Shower Mixing Valve', 'Shower Slide Bar with Hose', 'Sink Basin', 'Sink Basin Rack', 'Sink Drain', 'Sink Faucet', 'Sink Faucet Handle', 'Soap Dispenser', 'Toilet', 'Toilet Flush Handle', 'Toilet Seat', 'Wall Hung Toilet Concealed Tank'],
  'Bath Accessory': ['Towel Bar', 'Toilet Paper Holder', 'Wall Mounted Mirror', 'Robe Hook', 'Towel Ring', 'Soap Dispenser'],
  'Hardware Finish': ['Satin Nickel', 'Polished Chrome', 'Polished Nickel', 'Satin Brass', 'Oil Rubbed Bronze', 'Unlacquered Brass', 'Flat Black'],
  'Cabinet Construction': ['Face Frame', 'Frameless Full Access', 'Furniture Style'],
  'Door Mount Style': ['Full Overlay', 'Standard Inset', 'Beaded Inset'],
  'Panel Style': ['Recessed Beaded Edge', 'Skinny Recessed Beaded Edge', 'Recessed Square Edge', 'Skinny Recessed Square Edge', 'Shaker', 'Skinny Shaker', 'Slab'],
  'Panel Insert Types': ['Beadboard', 'Fluted', 'Glass', 'Rattan', 'Reeded', 'Slab', 'Wire Mesh'],
  'Primary Material Species': ['White Oak Rift Cut', 'White Oak Plain Sawn', 'Walnut Rift Cut', 'Walnut Plain Sawn', 'Ash Rift Cut', 'Ash Plain Sawn', 'Mahogany', 'Sapele', 'Maple Clear Finish', 'Cherry Clear Finish', 'Alder Clear Finish', 'MDF Paint Grade', 'HPL Finish'],
  'Accent Material': ['MDF Paint Grade', 'HPL'],
  'Finish System': ['Clear Finish Low Sheen', 'Clear Finish Matte', 'Stained Finish Light Tone', 'Stained Finish Medium Tone', 'Stained Finish Dark Tone', 'Custom Stain Match', 'Painted Finish Standard Sheen', 'Painted Finish Matte', 'Painted Finish High Gloss', 'HPL Factory Finish'],
  'Casework Hardware Finish': ['Satin Nickel', 'Polished Nickel', 'Matte Black', 'Oil Rubbed Bronze', 'Aged Brass', 'Unlacquered Brass', 'Antique Pewter', 'Custom Match Sample'],
  'Casework Types': ['Base', 'Tall', 'Wall', 'Floating', 'Island'],
}

/** Read a column's current value from an item (structural columns map to top-level fields). */
export function tableCellValue(item, col) {
  if (col.kind === 'image') return (item.attrs && (item.attrs['Image URL'] || item.attrs['image_url'])) || ''
  if (col.kind === 'label') return item.item_label || ''
  if (col.kind === 'section') return item.spec_section || ''
  if (col.kind === 'room') return item.room_string || ''
  if (col.kind === 'linkedItems') {
    const arr = item.attrs && Array.isArray(item.attrs[col.key]) ? item.attrs[col.key] : []
    return arr.map(r => r.label).join(', ')
  }
  return (item.attrs && item.attrs[col.key] != null) ? String(item.attrs[col.key]) : ''
}
